import { useState } from 'react'
import { Lock, Plus, Mail, Twitter, Linkedin, Facebook, Globe, Key, Server, Database, Smartphone } from 'lucide-react'
import { AddButton, ActionControls, Modal, ConfirmDialog, FormInput, FormSelect, FormTextarea } from '../components/common/CRUDControls'
import { useAccountsStore } from '../store/accountsStore'

export default function AccountsManagementPage() {
  // Use accounts store
  const accounts = useAccountsStore((state) => state.accounts)
  const addAccount = useAccountsStore((state) => state.addAccount)
  const updateAccount = useAccountsStore((state) => state.updateAccount)
  const deleteAccount = useAccountsStore((state) => state.deleteAccount)

  // Modal States
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'social_media',
    service: 'Twitter',
    username: '',
    password: '',
    apiKey: '',
    apiSecret: '',
    notes: '',
    status: 'Active',
  })

  const accountTypes = [
    { value: 'social_media', label: 'Social Media' },
    { value: 'email', label: 'Email Account' },
    { value: 'payment', label: 'Payment Gateway' },
    { value: 'api', label: 'API Integration' },
    { value: 'database', label: 'Database' },
    { value: 'hosting', label: 'Hosting/Cloud' },
    { value: 'other', label: 'Other' },
  ]

  const servicesList = {
    social_media: ['Twitter', 'Facebook', 'LinkedIn', 'Instagram', 'TikTok', 'Discord', 'Slack'],
    email: ['Gmail', 'Outlook', 'Proton Mail', 'iCloud', 'Custom SMTP'],
    payment: ['Stripe', 'PayPal', 'Square', '2Checkout'],
    api: ['OpenAI', 'Twilio', 'SendGrid', 'Auth0'],
    database: ['PostgreSQL', 'MongoDB', 'MySQL', 'Firebase'],
    hosting: ['AWS', 'Google Cloud', 'Azure', 'DigitalOcean', 'Heroku'],
    other: ['Other'],
  }

  const serviceIcons = {
    Twitter: <Twitter className="w-5 h-5" />,
    Facebook: <Facebook className="w-5 h-5" />,
    LinkedIn: <Linkedin className="w-5 h-5" />,
    Gmail: <Mail className="w-5 h-5" />,
    Stripe: <Key className="w-5 h-5" />,
    Database: <Database className="w-5 h-5" />,
  }

  // Handlers
  const handleAddAccount = () => {
    setFormData({
      name: '',
      type: 'social_media',
      service: 'Twitter',
      username: '',
      password: '',
      apiKey: '',
      apiSecret: '',
      notes: '',
      status: 'Active',
    })
    setSelectedAccount(null)
    setShowAddModal(true)
  }

  const handleEditAccount = (account) => {
    setSelectedAccount(account)
    setFormData(account)
    setShowEditModal(true)
  }

  const handleDeleteAccount = (account) => {
    setSelectedAccount(account)
    setShowDeleteConfirm(true)
  }

  const handleSaveAccount = () => {
    if (selectedAccount) {
      updateAccount(selectedAccount.id, formData)
      setShowEditModal(false)
    } else {
      addAccount(formData)
      setShowAddModal(false)
    }
  }

  const handleConfirmDelete = () => {
    deleteAccount(selectedAccount.id)
    setShowDeleteConfirm(false)
  }

  const getAccountIcon = (service) => {
    const iconMap = {
      'Twitter': <Twitter className="w-5 h-5 text-blue-400" />,
      'Facebook': <Facebook className="w-5 h-5 text-blue-500" />,
      'LinkedIn': <Linkedin className="w-5 h-5 text-blue-600" />,
      'Instagram': <Smartphone className="w-5 h-5 text-pink-400" />,
      'Gmail': <Mail className="w-5 h-5 text-red-400" />,
      'Outlook': <Mail className="w-5 h-5 text-blue-400" />,
      'Discord': <Globe className="w-5 h-5 text-indigo-500" />,
      'Slack': <Globe className="w-5 h-5 text-purple-500" />,
      'Stripe': <Key className="w-5 h-5 text-indigo-600" />,
      'PayPal': <Key className="w-5 h-5 text-blue-700" />,
      'AWS': <Server className="w-5 h-5 text-orange-500" />,
      'MongoDB': <Database className="w-5 h-5 text-green-500" />,
    }
    return iconMap[service] || <Globe className="w-5 h-5 text-gray-400" />
  }

  // Stats
  const stats = {
    total: accounts.length,
    connected: accounts.filter(a => a.status === 'Connected').length,
    socialMedia: accounts.filter(a => a.type === 'social_media').length,
    apiKeys: accounts.filter(a => a.type === 'api').length,
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-blue-700 to-cyan-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Lock className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Accounts & Integrations</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Manage All Your Connected Accounts & API Keys</p>
                <p className="text-blue-200 text-sm mt-1">Social Media, Email, Payment Gateways, APIs, Databases & More</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.total}</div>
                <div className="text-sm text-blue-100">Connected Accounts</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Accounts', value: stats.total, icon: Lock, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Connected', value: stats.connected, icon: Globe, color: 'from-green-600 to-green-500', textColor: 'text-green-400' },
            { label: 'Social Media', value: stats.socialMedia, icon: Twitter, color: 'from-cyan-600 to-cyan-500', textColor: 'text-cyan-400' },
            { label: 'API Keys', value: stats.apiKeys, icon: Key, color: 'from-indigo-600 to-indigo-500', textColor: 'text-indigo-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Manage Accounts */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
              <Key className="w-6 h-6 text-blue-400" />
              <span>Your Accounts</span>
            </h2>
            <AddButton onClick={handleAddAccount} label="Add Account" />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Account Name</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Type</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Service</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Username</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Last Sync</th>
                  <th className="text-center py-3 px-4 text-blue-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        {getAccountIcon(account.service)}
                        <span className="text-white font-semibold">{account.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-blue-300 text-sm">
                        {account.type.replace('_', ' ').charAt(0).toUpperCase() + account.type.replace('_', ' ').slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-blue-300">{account.service}</td>
                    <td className="py-3 px-4 text-gray-400 font-mono text-sm">{account.username}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        account.status === 'Connected' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {account.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-blue-300 text-sm">{account.lastSync}</td>
                    <td className="py-3 px-4 flex items-center justify-center">
                      <ActionControls
                        onEdit={() => handleEditAccount(account)}
                        onDelete={() => handleDeleteAccount(account)}
                        size="lg"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Account Categories Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              title: '📱 Social Media',
              description: 'Twitter, Facebook, LinkedIn, Instagram, Discord, Slack',
              count: stats.socialMedia,
              color: 'from-blue-600 to-blue-500'
            },
            {
              title: '✉️ Email Accounts',
              description: 'Gmail, Outlook, ProtonMail, iCloud, Custom SMTP',
              count: accounts.filter(a => a.type === 'email').length,
              color: 'from-red-600 to-red-500'
            },
            {
              title: '💳 Payment Gateways',
              description: 'Stripe, PayPal, Square, 2Checkout',
              count: accounts.filter(a => a.type === 'payment').length,
              color: 'from-green-600 to-green-500'
            },
            {
              title: '🔑 API Keys',
              description: 'OpenAI, Twilio, SendGrid, Auth0, Custom APIs',
              count: stats.apiKeys,
              color: 'from-indigo-600 to-indigo-500'
            },
            {
              title: '🗄️ Databases',
              description: 'PostgreSQL, MongoDB, MySQL, Firebase',
              count: accounts.filter(a => a.type === 'database').length,
              color: 'from-cyan-600 to-cyan-500'
            },
            {
              title: '☁️ Cloud/Hosting',
              description: 'AWS, Google Cloud, Azure, DigitalOcean, Heroku',
              count: accounts.filter(a => a.type === 'hosting').length,
              color: 'from-orange-600 to-orange-500'
            },
          ].map((cat, idx) => (
            <div key={idx} className={`bg-gradient-to-br ${cat.color} rounded-xl p-6 text-white shadow-lg`}>
              <h3 className="text-lg font-bold mb-2">{cat.title}</h3>
              <p className="text-white/80 text-sm mb-4">{cat.description}</p>
              <div className="flex items-center justify-between pt-4 border-t border-white/20">
                <span className="text-sm font-semibold">{cat.count} accounts</span>
              </div>
            </div>
          ))}
        </div>

        {/* Security Notes */}
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <Lock className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-orange-300 mb-2">🔒 Security Tips</h3>
              <ul className="text-orange-200/80 text-sm space-y-1">
                <li>• Store sensitive data like API keys and passwords securely</li>
                <li>• Regularly rotate your API keys and passwords</li>
                <li>• Never commit credentials to version control</li>
                <li>• Enable two-factor authentication where available</li>
                <li>• Monitor connected accounts for suspicious activity</li>
              </ul>
            </div>
          </div>
        </div>

      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false)
          setShowEditModal(false)
        }}
        title={selectedAccount ? 'Edit Account' : 'Add New Account'}
        onSubmit={handleSaveAccount}
        submitLabel={selectedAccount ? 'Update' : 'Add Account'}
      >
        <FormInput
          label="Account Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Twitter @Company"
          required
        />
        <FormSelect
          label="Account Type"
          value={formData.type}
          onChange={(e) => {
            const newType = e.target.value
            setFormData({ ...formData, type: newType, service: servicesList[newType]?.[0] || 'Other' })
          }}
          options={accountTypes}
          required
        />
        <FormSelect
          label="Service"
          value={formData.service}
          onChange={(e) => setFormData({ ...formData, service: e.target.value })}
          options={(servicesList[formData.type] || []).map(s => ({ value: s, label: s }))}
          required
        />
        <FormInput
          label="Username/Email"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          placeholder="e.g., user@example.com or @username"
          required
        />
        <FormInput
          label="Password/Token"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="Enter password or token"
        />
        <FormInput
          label="API Key"
          value={formData.apiKey}
          onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
          placeholder="e.g., sk_live_XXXXX"
        />
        <FormInput
          label="API Secret"
          value={formData.apiSecret}
          onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
          placeholder="e.g., secret_XXXXX"
        />
        <FormSelect
          label="Status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          options={[
            { value: 'Active', label: 'Active' },
            { value: 'Connected', label: 'Connected' },
            { value: 'Inactive', label: 'Inactive' },
            { value: 'Error', label: 'Error' },
          ]}
        />
        <FormTextarea
          label="Notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Add any notes about this account..."
          rows={3}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Delete Account"
        message={`Are you sure you want to delete "${selectedAccount?.name}"? This action cannot be undone.`}
      />
    </div>
  )
}
